import { SITE_TIME_ZONE } from "@/lib/site-time";
import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AdminNav } from "@/components/admin/AdminNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  MODULE_LABELS,
  PAGE_LABELS,
  PermissionGroup,
  collectEntries,
  emptyGroup,
  type GroupState,
} from "@/components/admin/PermissionGroup";
import { InviteUserForm } from "@/components/admin/InviteUserForm";
import { InvitationList } from "@/components/admin/InvitationList";
import {
  MODULE_KEYS,
  PAGE_KEYS,
  adminSaveUserPermissions,
  adminSetUserActive,
  adminSetUserAdmin,
  type AdminUserRow,
  type PermissionEntry,
} from "@/lib/users.functions";
import { adminUserPermissionsQuery, adminUsersQuery } from "@/lib/users-queries";
import { PERMANENT_ADMIN_EMAIL } from "@/lib/admin-access";

export const Route = createFileRoute("/_authenticated/admin/gebruikers")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Gebruikers en rechten — Gopro Projects" },
      { name: "description", content: "Beheer wie toegang heeft tot de website en welke pagina's en modules hij mag wijzigen." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Gebruikers en rechten — Gopro Projects" },
      { property: "og:description", content: "Beheer wie toegang heeft tot de website en welke pagina's en modules hij mag wijzigen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const [search, setSearch] = useState("");
  const [onlyActive, setOnlyActive] = useState<"alle" | "actief" | "inactief">("alle");
  const [selected, setSelected] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery(adminUsersQuery);

  const setActive = useMutation({
    mutationFn: (input: { userId: string; actief: boolean }) => adminSetUserActive({ data: input }),
    onSuccess: (_result, input) => {
      toast.success(input.actief ? "Gebruiker staat weer op actief." : "Gebruiker staat op niet actief.");
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Wijzigen is niet gelukt."),
  });

  const setAdmin = useMutation({
    mutationFn: (input: { userId: string; isAdmin: boolean }) => adminSetUserAdmin({ data: input }),
    onSuccess: (_result, input) => {
      toast.success(input.isAdmin ? "Deze gebruiker is nu beheerder." : "Deze gebruiker is geen beheerder meer.");
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Wijzigen is niet gelukt."),
  });

  const users = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data ?? []).filter((user) => {
      const matchesTerm = term === "" || user.naam.toLowerCase().includes(term) || user.email.toLowerCase().includes(term);
      const matchesActive = onlyActive === "alle" || (onlyActive === "actief" ? user.actief : !user.actief);
      return matchesTerm && matchesActive;
    });
  }, [data, search, onlyActive]);

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6">
      <AdminNav />
      <h1 className="text-3xl font-extrabold">Gebruikers en rechten</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Bepaal wie toegang heeft en welke pagina's en modules iemand mag wijzigen of publiceren.
      </p>

      <InviteUserForm />
      <InvitationList />

      <h2 className="mt-10 text-xl font-semibold">Gebruikers</h2>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="min-w-60 flex-1">
          <Label htmlFor="zoek-gebruiker">Zoeken op naam of e-mail</Label>
          <Input
            id="zoek-gebruiker"
            className="mt-1"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Bijvoorbeeld: Jan of jan@voorbeeld.be"
          />
        </div>
        <div className="flex gap-2">
          {(["alle", "actief", "inactief"] as const).map((value) => (
            <Button
              key={value}
              size="sm"
              variant={onlyActive === value ? "default" : "outline"}
              onClick={() => setOnlyActive(value)}
              aria-pressed={onlyActive === value}
            >
              {value === "alle" ? "Alle" : value === "actief" ? "Actief" : "Niet actief"}
            </Button>
          ))}
        </div>
      </div>

      {isLoading && <p className="mt-6 text-sm text-muted-foreground">Gebruikers laden…</p>}
      {isError && <p className="mt-6 text-sm text-destructive">De lijst met gebruikers kon niet geladen worden.</p>}
      {!isLoading && !isError && users.length === 0 && (
        <p className="mt-6 text-sm text-muted-foreground">Geen gebruikers gevonden in deze selectie.</p>
      )}

      <div className="mt-6 space-y-3">
        {users.map((user) => (
          <Card key={user.id}>
            <CardContent className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-semibold">{user.naam || "Naam niet ingevuld"}</h2>
                  <p className="text-sm text-muted-foreground">{user.email || "E-mail niet bekend"}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {user.isAdmin && <Badge>Beheerder</Badge>}
                    <Badge variant="secondary">
                      {user.permissionCount === 1 ? "1 toegekend recht" : `${user.permissionCount} toegekende rechten`}
                    </Badge>
                    <Badge variant="outline">
                      Aangemaakt op {new Intl.DateTimeFormat("nl-BE", { dateStyle: "medium", timeZone: SITE_TIME_ZONE }).format(new Date(user.aangemaaktOp))}
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {user.email?.toLowerCase() === PERMANENT_ADMIN_EMAIL && (
                    <span className="text-sm text-muted-foreground">Dit account blijft altijd beheerder.</span>
                  )}
                  <span className="text-sm">Beheerder</span>
                  <Switch
                    checked={user.isAdmin}
                    disabled={setAdmin.isPending || user.email?.toLowerCase() === PERMANENT_ADMIN_EMAIL}
                    aria-label={`Beheerdersrol voor ${user.naam || user.email} ${user.isAdmin ? "afnemen" : "toekennen"}`}
                    onCheckedChange={(checked) => {
                      const vraag = checked
                        ? `${user.naam || user.email} beheerder maken? Een beheerder kan alles wijzigen, ook gebruikers.`
                        : `${user.naam || user.email} de beheerdersrol afnemen?`;
                      if (window.confirm(vraag)) setAdmin.mutate({ userId: user.userId, isAdmin: checked });
                    }}
                  />
                  <span className="text-sm">{user.actief ? "Actief" : "Niet actief"}</span>
                  <Switch
                    checked={user.actief}
                    disabled={setActive.isPending || user.email?.toLowerCase() === PERMANENT_ADMIN_EMAIL}
                    aria-label={`Toegang voor ${user.naam || user.email} ${user.actief ? "uitschakelen" : "inschakelen"}`}
                    onCheckedChange={(checked) => setActive.mutate({ userId: user.userId, actief: checked })}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    aria-expanded={selected === user.userId}
                    onClick={() => setSelected(selected === user.userId ? null : user.userId)}
                  >
                    {selected === user.userId ? "Rechten sluiten" : "Rechten beheren"}
                  </Button>
                </div>
              </div>
              {selected === user.userId && <PermissionEditor user={user} />}
            </CardContent>
          </Card>
        ))}
      </div>
    </main>
  );
}

function PermissionEditor({ user }: { user: AdminUserRow }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({ ...adminUserPermissionsQuery(user.userId), enabled: !user.isAdmin });
  const [pages, setPages] = useState<GroupState>(() => emptyGroup(PAGE_KEYS));
  const [modules, setModules] = useState<GroupState>(() => emptyGroup(MODULE_KEYS));

  useEffect(() => {
    if (!data) return;
    const next = { page: emptyGroup(PAGE_KEYS), module: emptyGroup(MODULE_KEYS) };
    for (const entry of data) {
      const group = entry.resourceType === "page" ? next.page : entry.resourceType === "module" ? next.module : null;
      if (!group) continue;
      if (entry.resourceKey === "*") group.all = entry.permission;
      else group.keys[entry.resourceKey] = entry.permission;
    }
    setPages(next.page);
    setModules(next.module);
  }, [data]);

  const save = useMutation({
    mutationFn: (entries: PermissionEntry[]) => adminSaveUserPermissions({ data: { userId: user.userId, entries } }),
    onSuccess: () => {
      toast.success("De rechten van deze gebruiker zijn opgeslagen.");
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-user-permissions", user.userId] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Opslaan is niet gelukt."),
  });

  if (user.isAdmin) {
    return (
      <p className="mt-5 rounded border border-border bg-muted/40 p-4 text-sm">
        Deze gebruiker is beheerder en heeft altijd toegang tot alle pagina's en modules. Er zijn dus geen losse rechten in te stellen.
      </p>
    );
  }

  if (isLoading) return <p className="mt-5 text-sm text-muted-foreground">Rechten laden…</p>;
  if (isError) return <p className="mt-5 text-sm text-destructive">De rechten van deze gebruiker konden niet geladen worden.</p>;

  const collect = () => collectEntries(pages, modules);

  return (
    <div className="mt-5 border-t border-border pt-5">
      <p className="text-sm text-muted-foreground">
        Publiceren bevat wijzigen, wijzigen bevat lezen. U kiest dus één niveau per regel, geen drie losse vinkjes.
      </p>
      <div className="mt-5 grid gap-6 lg:grid-cols-2">
        <PermissionGroup
          title="Pagina's"
          allLabel="Alle pagina's"
          keys={PAGE_KEYS}
          labels={PAGE_LABELS}
          state={pages}
          onChange={setPages}
        />
        <PermissionGroup
          title="Modules"
          allLabel="Alle modules"
          keys={MODULE_KEYS}
          labels={MODULE_LABELS}
          state={modules}
          onChange={setModules}
        />
      </div>
      <div className="mt-6">
        <Button disabled={save.isPending} onClick={() => save.mutate(collect())}>
          {save.isPending ? "Bezig met opslaan…" : "Rechten opslaan"}
        </Button>
      </div>
    </div>
  );
}

